import {useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {dataClient} from '../data/dataClient';
import {providerOptions,resolveProviderBaseUrlForOption,testProviderConnection} from '../services/providerTest';

const {FiCheckCircle,FiEdit2,FiKey,FiPlus,FiTrash2,FiXCircle}=FiIcons;

const emptyForm={
  provider: 'openrouter',
  label: '',
  apiKey: '',
  endpoint: '',
  model: ''
};

function maskKey(key) {
  if (!key) return 'No key saved';
  return `${key.slice(0,4)}${'•'.repeat(Math.max(4,Math.min(16,key.length - 4)))}`;
}

function ConnectionForm({initial,onCancel,onSaved}) {
  const [form,setForm]=useState({...emptyForm,...initial});
  const [saving,setSaving]=useState(false);
  const [saveError,setSaveError]=useState(null);
  const provider=providerOptions.find((item)=> item.id===form.provider);

  function change(field,value) {
    setForm((current)=> ({...current,[field]: value}));
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);

    // Resolve the effective base URL at save time (fix #2): known providers
    // get their OpenAI-compatible base from the map; `custom` keeps the
    // user-supplied endpoint. `model` is stored as typed — the direct call
    // sends exactly this id (model-id honesty: no hidden alias rewriting).
    const base = resolveProviderBaseUrlForOption(form.provider, form.endpoint);

    const record={
      ...form,
      label: form.label.trim() || provider.name,
      model: form.model.trim() || provider.defaultModel,
      endpoint: base || '',
      // Non-custom providers store the resolved base so test + chat agree.
      _providerBase: base || null,
      enabled: initial?.enabled ?? true,
      updated: new Date().toISOString()
    };

    if (!record.apiKey.trim()) {
      setSaving(false);
      return;
    }

    if (form.provider !== 'custom' && !record.endpoint) {
      setSaveError(
        `No known OpenAI-compatible endpoint for "${provider.name}". Add it as a Custom provider with an explicit endpoint.`
      );
      setSaving(false);
      return;
    }

    try {
      const saved=initial?.id
        ? await dataClient.update('llmProviderCredentials',initial.id,record)
        : await dataClient.create('llmProviderCredentials',record);

      onSaved(saved);
    } catch (err) {
      setSaveError(err?.message || 'Could not save the connection.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="provider-form" onSubmit={save}>
      <div className="form-grid">
        <label>
          Provider
          <select
            value={form.provider}
            onChange={(event)=> change('provider',event.target.value)}
          >
            {providerOptions.map((item)=> (
              <option value={item.id} key={item.id}>{item.name}</option>
            ))}
          </select>
        </label>
        <label>
          Label
          <input
            value={form.label}
            onChange={(event)=> change('label',event.target.value)}
            placeholder="My production provider"
          />
        </label>
        <label>
          API key
          <input
            required
            type="password"
            value={form.apiKey}
            onChange={(event)=> change('apiKey',event.target.value)}
            placeholder={initial ? 'Enter a new key to replace it' : 'sk-...'}
          />
        </label>
        <label>
          Model
          <input
            value={form.model}
            onChange={(event)=> change('model',event.target.value)}
            placeholder={provider.defaultModel || 'provider/model-name'}
          />
        </label>
        {form.provider==='custom' && (
          <label className="full">
            Custom endpoint
            <input
              required
              type="url"
              value={form.endpoint}
              onChange={(event)=> change('endpoint',event.target.value)}
              placeholder="https://api.example.com/v1"
            />
          </label>
        )}
      </div>
      {form.provider!=='custom' && (
        <p className="form-hint">
          Calls go to <code>{resolveProviderBaseUrlForOption(form.provider, form.endpoint) || '…'}</code>{' '}
          with model <code>{form.model.trim() || provider.defaultModel}</code>. Only this browser holds
          the key — never the server.
        </p>
      )}
      {saveError && <p className="form-error" role="alert">{saveError}</p>}
      <div className="inline">
        <button className="button primary" type="submit" disabled={saving}>
          <SafeIcon icon={FiCheckCircle} />
          {saving ? 'Saving…' : 'Save connection'}
        </button>
        <button className="button ghost" type="button" onClick={onCancel}>
          <SafeIcon icon={FiXCircle} />
          Cancel
        </button>
      </div>
    </form>
  );
}

function ConnectionRow({connection,onEdit,onDeleted}) {
  const [testing,setTesting]=useState(false);
  const [result,setResult]=useState(null);

  // Real round-trip (fix #2): GET {base}/models with the stored key. Never a
  // timeout-based fake — OK means the endpoint authenticated AND listed models.
  async function testConnection() {
    setTesting(true);
    setResult(null);
    try {
      const outcome = await testProviderConnection(connection);
      setResult({ ok: outcome.ok, message: outcome.message });
    } catch (err) {
      setResult({ ok: false, message: err?.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function toggle() {
    await dataClient.update(
      'llmProviderCredentials',
      connection.id,
      {enabled: !connection.enabled}
    );
  }

  async function remove() {
    await dataClient.remove('llmProviderCredentials',connection.id);
    onDeleted();
  }

  return (
    <article className="provider-row">
      <span className="file-icon"><SafeIcon icon={FiKey} /></span>
      <div>
        <b>{connection.label}</b>
        <small>
          {providerOptions.find((item)=> item.id===connection.provider)?.name}
          {' · '}
          {connection.model}
          {connection.endpoint ? ` · ${connection.endpoint}` : ''}
        </small>
        <small>{maskKey(connection.apiKey)}</small>
        {result && (
          <span className={result.ok ? 'test-success' : 'test-failure'}>
            <SafeIcon icon={result.ok ? FiCheckCircle : FiXCircle} />
            {result.ok ? 'Connection test passed' : result.message}
          </span>
        )}
      </div>
      <div className="provider-actions">
        <button className="button compact secondary" onClick={testConnection}>
          {testing ? 'Testing…' : 'Test'}
        </button>
        <button className="icon-button" onClick={()=> onEdit(connection)}>
          <SafeIcon icon={FiEdit2} />
        </button>
        <button className="icon-button" onClick={remove}>
          <SafeIcon icon={FiTrash2} />
        </button>
        <label className="toggle">
          <input
            type="checkbox"
            checked={connection.enabled}
            onChange={toggle}
          />
          <span />
        </label>
      </div>
    </article>
  );
}

export default function ProviderConnections({connections=[]}) {
  const [editing,setEditing]=useState(null);
  const [adding,setAdding]=useState(false);

  return (
    <section className="panel provider-panel">
      <div className="section-heading">
        <div>
          <span className="eyebrow">BYO provider</span>
          <h2>Connected LLM providers</h2>
        </div>
        <button
          className="button primary compact"
          onClick={()=> setAdding(true)}
        >
          <SafeIcon icon={FiPlus} />
          Connect a provider
        </button>
      </div>

      {(adding || editing) && (
        <ConnectionForm
          initial={editing}
          onCancel={()=> {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={()=> {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}

      {!connections.length && !adding && (
        <p className="empty-copy">No provider credentials are connected yet. Connect your own API key — chat for every bot runs through your providers; the platform does not host a model tier.</p>
      )}

      <div className="provider-list">
        {connections.map((connection)=> (
          <ConnectionRow
            key={connection.id}
            connection={connection}
            onEdit={(item)=> {
              setEditing(item);
              setAdding(false);
            }}
            onDeleted={()=> {
              if (editing?.id===connection.id) setEditing(null);
            }}
          />
        ))}
      </div>
    </section>
  );
}